<template>
  <div class="w-full h-full flex flex-col gap-4">
    <!-- Transaction history for one currency. Opened from a balance tile, so it always
         answers "where did this balance come from". -->
    <Drawer
      direction="right"
      :open="showHistory"
      @update:open="(o) => { if (!o) closeHistory() }"
    >
      <DrawerContent class="data-[vaul-drawer-direction=right]:sm:max-w-3xl">
        <DrawerHeader class="text-left">
          <DrawerTitle>Transaction history — {{ historyCurrency }}</DrawerTitle>
          <DrawerDescription>
            Every change to this balance, newest first. Entries are append-only, so this is the
            record of where the money came from and went.
          </DrawerDescription>
        </DrawerHeader>

        <div class="flex-1 overflow-y-auto px-4">
          <DataTable
            :headers="historyHeaders"
            :items="history"
            :loading="historyLoading"
            :server-options="historyOptions"
            :server-items-length="historyTotal"
            @update:server-options="onHistoryOptions"
          >
            <template #empty-message>
              <div class="flex justify-center py-4 text-muted-foreground text-sm">
                No transactions for this currency yet.
              </div>
            </template>

            <template #item-type="row">
              <Badge :variant="typeVariant(row.type)">{{ row.type }}</Badge>
            </template>

            <template #item-amount="row">
              <span
                class="text-sm tabular-nums font-medium"
                :class="Number(row.amountMinorUnits) < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'"
              >
                {{ Number(row.amountMinorUnits) < 0 ? '' : '+' }}{{ formatAmount(row.amountMinorUnits, row.currency) }}
              </span>
            </template>

            <template #item-balanceAfter="row">
              <span class="text-sm tabular-nums text-muted-foreground">
                {{ formatAmount(row.balanceAfterMinorUnits, row.currency) }}
              </span>
            </template>

            <template #item-reference="row">
              <div class="flex flex-col">
                <span v-if="row.reference" class="text-sm">{{ row.reference }}</span>
                <span v-if="row.note" class="text-xs text-muted-foreground">{{ row.note }}</span>
                <span v-if="!row.reference && !row.note" class="text-sm text-muted-foreground">—</span>
              </div>
            </template>

            <template #item-createdOn="row">
              <FormatDate :value="row.createdOn" format="datetime" />
            </template>
          </DataTable>
        </div>

        <DrawerFooter class="flex-row justify-end">
          <Button variant="outline" @click="closeHistory()">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <!-- Shown only when the publisher is not entitled; the rest of the page is hidden then. -->
    <Card v-if="!enabled">
      <CardHeader>
        <CardTitle>Gift cards</CardTitle>
        <CardDescription>
          Gift cards are configured by Boogi. This page shows what has been made available to you.
        </CardDescription>
        <CardAction>
          <Badge variant="secondary">Not enabled</Badge>
        </CardAction>
      </CardHeader>
      <CardContent>
        <p class="text-sm text-muted-foreground">
          Gift cards are not enabled for your account. Contact your account manager to have them
          switched on.
        </p>
      </CardContent>
    </Card>

    <!-- Prepaid balance -->
    <Card v-if="enabled">
      <CardHeader>
        <CardTitle>Gift card balance</CardTitle>
        <CardDescription>
          Prepaid funds for gift card purchases. Purchases are charged at the card's face value, so
          your balance spends one for one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div v-if="balances.length === 0" class="text-sm text-muted-foreground">
          No funds have been added to your account yet. Contact your account manager to top up and
          start selling.
        </div>

        <div v-else class="grid gap-4 sm:grid-cols-2">
          <!-- Each tile opens the history for its own currency. -->
          <div
            v-for="balance in balances"
            :key="balance.currency"
            class="rounded-lg border border-border px-4 py-3"
          >
            <button
              type="button"
              class="w-full text-left focus-visible:outline-none"
              @click="openHistory(balance)"
            >
              <div class="flex items-baseline justify-between gap-2">
                <span class="text-xs font-medium text-muted-foreground">{{ balance.currency }}</span>
                <span class="text-xs text-muted-foreground">View history</span>
              </div>
              <p class="mt-1 text-2xl font-semibold tabular-nums">
                {{ formatAmount(balance.balanceMinorUnits, balance.currency) }}
              </p>
              <p class="mt-0.5 text-xs text-muted-foreground">
                available to spend at face value
              </p>
            </button>

            <!--
              Frozen: the balance cannot cover the orders already waiting, so nothing new can be
              funded until it is topped up. Shown above the warning line because it is the more
              urgent of the two.
            -->
            <div
              v-if="balance.queueFrozen"
              class="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2"
            >
              <p class="text-sm font-medium text-destructive">Purchases paused</p>
              <p class="mt-0.5 text-xs text-muted-foreground">
                {{ formatAmount(balance.outstandingQueuedAmountMinorUnits, balance.currency) }} of
                orders are waiting on funds. Add at least that much to resume — they will be
                processed automatically, oldest first.
              </p>
            </div>

            <!-- Warning line: a top-up prompt, not a spending limit. -->
            <div class="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
              <div class="min-w-0">
                <p class="text-xs text-muted-foreground">Low balance alert</p>
                <p class="text-sm tabular-nums">
                  <template v-if="balance.lowBalanceThresholdMinorUnits != null">
                    {{ formatAmount(balance.lowBalanceThresholdMinorUnits, balance.currency) }}
                  </template>
                  <span v-else class="text-muted-foreground">Not set</span>
                </p>
                <p
                  v-if="balance.lowBalanceThresholdManagedByBoogi"
                  class="text-xs text-muted-foreground"
                >
                  Boogi alerts at
                  {{ formatAmount(balance.effectiveLowBalanceThresholdMinorUnits, balance.currency) }}
                </p>
              </div>
              <Button variant="outline" size="sm" @click="openThreshold(balance)">
                {{ balance.lowBalanceThresholdMinorUnits != null ? 'Change' : 'Set' }}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <!-- Available gift cards -->
    <Card v-if="enabled">
      <CardHeader>
        <CardTitle>Available gift cards</CardTitle>
        <CardDescription>
          Activate the brands you want to sell. Only activated brands are published to your app
          through the public API.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headers"
          :items="catalogue"
          :loading="loading"
          :error="error"
          :server-options="serverOptions"
          :server-items-length="total"
          searchable
          search-placeholder="Search by brand name or code"
          :search="searchInput"
          :filter-groups="filterGroups"
          :filters="filters"
          @update:server-options="onServerOptions"
          @update:search="searchInput = $event"
          @search="onSearch"
          @clear-search="onClearSearch"
          @filter-change="onFilterChange"
          @retry="getCatalogue()"
        >
          <template #empty-message>
            <div class="flex justify-center py-4 text-muted-foreground text-sm">
              No gift cards match your filters
            </div>
          </template>

          <template #header-brand>
            <SortHeader label="Gift card" field="name" :sort-by="sortBy" :sort-dir="sortDir" @sort="onSort" />
          </template>
          <template #header-currency>
            <SortHeader label="Currency" field="currency" :sort-by="sortBy" :sort-dir="sortDir" @sort="onSort" />
          </template>
          <template #header-country>
            <SortHeader label="Country" field="countryCode" :sort-by="sortBy" :sort-dir="sortDir" @sort="onSort" />
          </template>
          <template #header-value>
            <SortHeader label="Value" field="minPriceInCents" :sort-by="sortBy" :sort-dir="sortDir" @sort="onSort" />
          </template>

          <template #item-brand="brand">
            <div class="flex items-center gap-2.5">
              <img
                v-if="brand.imageUrl"
                class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                :src="brand.imageUrl"
                alt=""
              />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate" :title="brand.name">{{ brand.name }}</p>
                <p class="text-xs text-muted-foreground mt-1 truncate">{{ brand.brandCode }}</p>
              </div>
            </div>
          </template>

          <template #item-currency="brand">
            <Badge v-if="brand.currency" variant="outline">{{ brand.currency }}</Badge>
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-country="brand">
            <!-- The market the card is sold in. Blank for the provider's "Global" bucket, which
                 is a rest-of-world grouping rather than a country. -->
            <span v-if="brand.countryCode" class="text-sm whitespace-nowrap">
              {{ formatCountry(brand.countryCode) }}
            </span>
            <span v-else class="text-sm text-muted-foreground">—</span>
          </template>

          <template #item-discount="brand">
            <span v-if="brand.discount != null" class="text-sm tabular-nums whitespace-nowrap">
              {{ formatDiscount(brand.discount) }}
            </span>
            <span v-else class="text-sm text-muted-foreground whitespace-nowrap">—</span>
          </template>

          <template #item-denominations="brand">
            <div v-if="isFixed(brand)" class="flex items-center gap-1.5">
              <Badge variant="secondary">Fixed</Badge>
              <span class="text-xs text-muted-foreground tabular-nums">
                {{ brand.allowedValuesInCents.length }} option{{ brand.allowedValuesInCents.length === 1 ? '' : 's' }}
              </span>
            </div>
            <Badge v-else variant="outline">Any value</Badge>
          </template>

          <template #item-value="brand">
            <span v-if="isFixed(brand)" class="text-sm tabular-nums whitespace-nowrap">
              {{ formatAmount(brand.allowedValuesInCents[0], brand.currency) }} –
              {{ formatAmount(brand.allowedValuesInCents[brand.allowedValuesInCents.length - 1], brand.currency) }}
            </span>
            <span v-else-if="brand.minPriceInCents != null" class="text-sm tabular-nums whitespace-nowrap">
              {{ formatAmount(brand.minPriceInCents, brand.currency) }}<span v-if="brand.maxPriceInCents != null"> – {{ formatAmount(brand.maxPriceInCents, brand.currency) }}</span>
            </span>
            <span v-else class="text-sm text-muted-foreground whitespace-nowrap">Set on order</span>
          </template>

          <template #item-activated="brand">
            <div class="flex items-center gap-2">
              <Switch
                :model-value="brand.activated"
                :disabled="togglingId === brand.id"
                @update:model-value="(v) => setActivated(brand, v)"
              />
              <span class="text-xs text-muted-foreground whitespace-nowrap">
                {{ brand.activated ? 'Live' : 'Not published' }}
              </span>
            </div>
          </template>

          <template #item-action="brand">
            <div class="flex justify-end">
              <Button variant="outline" size="sm" @click="openDetail(brand)">Details</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>

    <!-- Low-balance threshold editor, scoped to one currency. -->
    <AlertDialog
      :open="showThreshold"
      @update:open="(o) => { if (!o) closeThreshold() }"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ `Low balance alert — ${thresholdCurrency}` }}</AlertDialogTitle>
          <AlertDialogDescription>
            Get a webhook when a purchase takes this balance to or below your warning line.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-foreground" for="low-balance-threshold-amount">
              Warn me below
            </label>
            <div class="flex items-center gap-2">
              <Input
                id="low-balance-threshold-amount"
                v-model="thresholdForm.amount"
                type="number"
                placeholder="e.g. 500"
              />
              <span class="text-sm text-muted-foreground">{{ thresholdCurrency }}</span>
            </div>
            <p class="text-xs text-muted-foreground">
              Leave blank to turn the alert off. This is a warning, not a limit — purchases still go
              through below it until the balance runs out.
            </p>
          </div>

          <p
            v-if="thresholdManagedByBoogi"
            class="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
          >
            Boogi has set an alert at
            <strong>{{ formatAmount(thresholdEffective, thresholdCurrency) }}</strong> for this
            currency. Your value is saved, but the alert will not fire on it until Boogi clears
            theirs.
          </p>

          <p class="text-xs text-muted-foreground">
            We send a <strong>GIFT_CARD_BALANCE_THRESHOLD_REACHED</strong> webhook each time a
            purchase crosses this line. Subscribe to it under
            <strong>Developers → Webhooks</strong>.
          </p>

          <p v-if="thresholdError" class="text-destructive text-sm">{{ thresholdError }}</p>
        </div>

        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="closeThreshold">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="saveThreshold">Save alert</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- Brand detail: the full denomination list, which is too long for a table cell. -->
    <Drawer :open="showDetail" direction="right" @update:open="(v) => !v && closeDetail()">
      <DrawerContent class="sm:max-w-lg">
        <DrawerHeader>
          <DrawerTitle>{{ detailBrand?.name }}</DrawerTitle>
          <DrawerDescription>{{ detailBrand?.brandCode }}</DrawerDescription>
        </DrawerHeader>

        <div v-if="detailBrand" class="flex flex-col gap-4 px-4 pb-4 overflow-y-auto">
          <div class="flex items-center gap-3">
            <img
              v-if="detailBrand.imageUrl"
              class="w-14 h-14 rounded-md border border-border object-contain bg-white"
              :src="detailBrand.imageUrl"
              alt=""
            />
            <div class="flex flex-wrap gap-1.5">
              <Badge v-if="detailBrand.currency" variant="outline">{{ detailBrand.currency }}</Badge>
              <Badge v-if="detailBrand.countryCode" variant="outline">
                {{ formatCountry(detailBrand.countryCode) }}
              </Badge>
              <Badge :variant="detailBrand.activated ? 'success' : 'secondary'">
                {{ detailBrand.activated ? 'Live' : 'Not published' }}
              </Badge>
            </div>
          </div>

          <div class="rounded-lg border border-border">
            <p class="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground border-b border-border">
              Denominations
            </p>
            <div class="px-3 py-3">
              <template v-if="isFixed(detailBrand)">
                <p class="text-sm text-muted-foreground mb-2">
                  This card can only be bought at these amounts.
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <Badge
                    v-for="value in detailBrand.allowedValuesInCents"
                    :key="value"
                    variant="secondary"
                    class="tabular-nums"
                  >{{ formatAmount(value, detailBrand.currency) }}</Badge>
                </div>
              </template>
              <template v-else-if="detailBrand.minPriceInCents != null">
                <p class="text-sm text-muted-foreground mb-1">Any amount between</p>
                <p class="text-sm font-medium tabular-nums">
                  {{ formatAmount(detailBrand.minPriceInCents, detailBrand.currency) }} –
                  {{ formatAmount(detailBrand.maxPriceInCents, detailBrand.currency) }}
                </p>
              </template>
              <p v-else class="text-sm text-muted-foreground">
                The amount is set when the order is placed.
              </p>
            </div>
          </div>

          <p v-if="detailBrand.description" class="text-sm text-muted-foreground">
            {{ detailBrand.description }}
          </p>

          <p v-if="detailBrand.disclaimer" class="text-xs text-muted-foreground">
            <span class="font-medium text-foreground">Disclaimer:</span>
            {{ stripHtml(detailBrand.disclaimer) }}
          </p>
        </div>

        <DrawerFooter>
          <Button variant="outline" @click="closeDetail">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Badge,
  Button,
  Switch,
  FormatDate,
  Input,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@boogi/ui'
import { DataTable, SortHeader, toast, formatRatePercent, formatCountry } from '@boogi/ui'

export default {
  name: 'gift-cards',
  components: {
    DataTable,
    SortHeader,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Badge,
    Button,
    Switch,
    FormatDate,
    Input,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
  },
  props: {},

  data() {
    return {
      loading: true,
      error: false,
      enabled: false,
      balances: [],
      headers: [
        { text: 'Gift card', value: 'brand', width: 300 },
        { text: 'Currency', value: 'currency', width: 110 },
        { text: 'Country', value: 'country', width: 170 },
        { text: 'Denominations', value: 'denominations', width: 150 },
        { text: 'Value', value: 'value', width: 190 },
        { text: 'Your discount', value: 'discount', width: 130 },
        { text: 'Published', value: 'activated', width: 170 },
        { text: 'Action', value: 'action', width: 110 },
      ],
      catalogue: [],

      // Server-side paging, search, sort and filters: the catalogue is paged so the table
      // cannot sort or filter a page it has not fetched.
      serverOptions: { page: 1, rowsPerPage: 25 },
      total: undefined,
      searchInput: '',
      appliedSearch: '',
      sortBy: 'name',
      sortDir: 'asc',
      selectedCurrencies: [],
      selectedCountries: [],
      selectedDenominations: [],
      selectedActivation: [],

      // Brand whose detail drawer is open.
      showDetail: false,
      detailBrand: null,

      // Low-balance threshold editor, scoped to one currency.
      showThreshold: false,
      thresholdCurrency: '',
      thresholdError: '',
      thresholdForm: { amount: '' },
      // Boogi's override for the currency being edited, if any. Informational only: the
      // publisher can still save their own value.
      thresholdManagedByBoogi: false,
      thresholdEffective: null,

      // Brand id currently being activated/deactivated, so its switch can be disabled.
      togglingId: '',

      // Transaction history drawer, scoped to one currency at a time.
      showHistory: false,
      historyCurrency: '',
      history: [],
      historyLoading: false,
      historyTotal: undefined,
      historyOptions: { page: 1, rowsPerPage: 25 },
      historyHeaders: [
        { text: 'Type', value: 'type', width: 110 },
        { text: 'Amount', value: 'amount', width: 130 },
        { text: 'Balance after', value: 'balanceAfter', width: 140 },
        { text: 'Reference', value: 'reference' },
        { text: 'When', value: 'createdOn', width: 160 },
      ],
    }
  },

  created() {
    this.getStatus()
  },

  computed: {
    /** Filter options for the catalogue. Currencies and countries come from the loaded page. */
    filterGroups() {
      const currencies = [...new Set(this.catalogue.map((b) => b.currency).filter(Boolean))].sort()
      const countries = [...new Set(this.catalogue.map((b) => b.countryCode).filter(Boolean))].sort()
      return [
        {
          key: 'denominations',
          label: 'Denominations',
          options: [
            { value: 'fixed', label: 'Fixed amounts' },
            { value: 'any', label: 'Any value' },
          ],
        },
        {
          key: 'activated',
          label: 'Published',
          options: [
            { value: 'true', label: 'Live' },
            { value: 'false', label: 'Not published' },
          ],
        },
        ...(countries.length > 1
          ? [
              {
                key: 'country',
                label: 'Country',
                options: countries.map((c) => ({ value: c, label: formatCountry(c) })),
              },
            ]
          : []),
        ...(currencies.length > 1
          ? [
              {
                key: 'currency',
                label: 'Currency',
                options: currencies.map((c) => ({ value: c, label: c })),
              },
            ]
          : []),
      ]
    },
    filters() {
      return {
        denominations: this.selectedDenominations,
        activated: this.selectedActivation,
        currency: this.selectedCurrencies,
        country: this.selectedCountries,
      }
    },
  },

  methods: {
    /** True when the brand only sells at a fixed set of amounts. */
    isFixed(brand) {
      return Array.isArray(brand.allowedValuesInCents) && brand.allowedValuesInCents.length > 0
    },

    /**
     * Renders a discount fraction as a percentage.
     *
     * <p>The API returns the publisher's effective cut as a fraction (e.g. 0.0168), which is the
     * brand's full margin multiplied by the publisher's own share. Floored to 2 dp so the rate a
     * publisher shows their customers is never higher than the rate they are actually paid.
     */
    formatDiscount(fraction) {
      return formatRatePercent(fraction)
    },

    /**
     * Flattens the disclaimer's markup to plain text.
     *
     * <p>The provider embeds HTML in this field. Rendering it raw would inject third-party markup
     * into the page, and the tags add nothing to the sentence a publisher reads.
     */
    stripHtml(value) {
      if (!value) return ''
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    onServerOptions(next) {
      this.serverOptions = next
      this.getCatalogue()
    },

    onSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDir = 'asc'
      }
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.getCatalogue()
    },

    onSearch(value) {
      this.appliedSearch = value
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.getCatalogue()
    },

    onClearSearch() {
      this.searchInput = ''
      this.appliedSearch = ''
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.getCatalogue()
    },

    onFilterChange(updated) {
      this.selectedDenominations = (updated && updated.denominations) || []
      this.selectedActivation = (updated && updated.activated) || []
      this.selectedCurrencies = (updated && updated.currency) || []
      this.selectedCountries = (updated && updated.country) || []
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.getCatalogue()
    },

    openDetail(brand) {
      this.detailBrand = brand
      this.showDetail = true
    },

    closeDetail() {
      this.showDetail = false
      this.detailBrand = null
    },

    /** Opens the low-balance alert editor for a currency. */
    openThreshold(balance) {
      this.thresholdCurrency = balance.currency
      this.thresholdError = ''
      this.thresholdManagedByBoogi = !!balance.lowBalanceThresholdManagedByBoogi
      this.thresholdEffective = balance.effectiveLowBalanceThresholdMinorUnits ?? null
      this.thresholdForm = {
        amount:
          balance.lowBalanceThresholdMinorUnits == null
            ? ''
            : String(Number(balance.lowBalanceThresholdMinorUnits) / 100),
      }
      this.showThreshold = true
    },

    closeThreshold() {
      this.showThreshold = false
      this.thresholdError = ''
      this.thresholdManagedByBoogi = false
      this.thresholdEffective = null
      // thresholdCurrency is deliberately NOT cleared here. AlertDialogAction closes the dialog
      // before its click handler runs, so clearing it would blank the currency out from under
      // saveThreshold and produce a request to /balances//threshold. It is overwritten by the
      // next openThreshold instead.
    },

    /** Saves the warning line. Blank clears it. */
    async saveThreshold() {
      this.thresholdError = ''
      // Captured up front: the dialog may already be closing by the time this runs.
      const currency = this.thresholdCurrency
      if (!currency) {
        this.thresholdError = 'Could not tell which currency to update. Please reopen the dialog.'
        return
      }
      const raw = this.thresholdForm.amount
      const amount = raw === '' || raw == null ? null : Number(raw)

      if (amount !== null) {
        if (Number.isNaN(amount)) {
          this.thresholdError = 'Enter a number.'
          return
        }
        if (amount < 0) {
          this.thresholdError = 'The alert amount cannot be negative.'
          return
        }
      }

      try {
        const res = await this.$apiFetch(
          `${this.apiUrl()}/private/api/client/gift-card/balances/${currency}/threshold`,
          {
            method: 'PUT',
            headers: this.authHeaders(),
            // The API works in minor units, so the major-unit input is converted here.
            body: JSON.stringify({
              thresholdMinorUnits: amount === null ? null : Math.round(amount * 100),
            }),
          }
        )
        if (res && res.ok) {
          const updated = await res.json()
          const index = this.balances.findIndex((b) => b.currency === updated.currency)
          if (index !== -1) this.balances.splice(index, 1, updated)
          toast.success(
            amount === null
              ? 'Low balance alert turned off'
              : `We'll alert you below ${this.formatAmount(updated.lowBalanceThresholdMinorUnits, updated.currency)}`
          )
          this.closeThreshold()
        } else {
          const text = await res.text().catch(() => '')
          this.thresholdError = text || 'Could not save the alert.'
        }
      } catch (e) {
        this.thresholdError = 'Could not reach the server. Please try again.'
      }
    },

    apiUrl() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },

    authHeaders() {
      const token = localStorage.getItem('token')
      return { 'Content-Type': 'application/json', Authorization: `${token}` }
    },

    async getStatus() {
      this.loading = true
      this.error = false
      try {
        // Entitlement comes from the publisher record; the catalogue call then reflects
        // whatever Boogi has made available.
        const publisherRes = await this.$apiFetch(`${this.apiUrl()}/private/api/client/publisher`, {
          headers: this.authHeaders(),
        })
        if (publisherRes && publisherRes.ok) {
          const publisher = await publisherRes.json()
          this.enabled =
            Array.isArray(publisher.products) && publisher.products.includes('GIFTCARDS')
        }

        if (this.enabled) {
          await this.getCatalogue()
          await this.getBalances()
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getCatalogue() {
      this.loading = true
      this.error = false
      try {
        const params = new URLSearchParams()
        params.append('page', (this.serverOptions.page || 1) - 1)
        params.append('size', this.serverOptions.rowsPerPage || 25)
        if (this.appliedSearch) params.append('search', this.appliedSearch)
        if (this.sortBy) params.append('sortBy', this.sortBy)
        if (this.sortDir) params.append('sortDir', this.sortDir)
        if (this.selectedCurrencies.length === 1) {
          params.append('currency', this.selectedCurrencies[0])
        }
        if (this.selectedCountries.length === 1) {
          params.append('countryCode', this.selectedCountries[0])
        }
        // The API takes a single boolean, so only a single selection can be expressed. Selecting
        // both is the same as selecting neither, so it is left off entirely.
        if (this.selectedDenominations.length === 1) {
          params.append('fixedDenominations', this.selectedDenominations[0] === 'fixed')
        }
        if (this.selectedActivation.length === 1) {
          params.append('activated', this.selectedActivation[0] === 'true')
        }

        const res = await this.$apiFetch(
          `${this.apiUrl()}/private/api/client/gift-card/catalogue?${params.toString()}`,
          { headers: this.authHeaders() }
        )
        if (res && res.ok) {
          const page = await res.json()
          this.catalogue = page.content || []
          this.total = page.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    /**
     * Renders a minor-unit amount as currency.
     *
     * <p>Gift card amounts arrive in minor units, so they are divided by 100 rather than assumed
     * to be major units already.
     */
    formatAmount(minorUnits, currency) {
      if (minorUnits == null) return '—'
      const amount = Number(minorUnits) / 100
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'GBP',
        }).format(amount)
      } catch {
        // An unrecognised currency code would throw; fall back to a plain number.
        return `${amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${currency || ''}`.trim()
      }
    },

    async getBalances() {
      try {
        const res = await this.$apiFetch(`${this.apiUrl()}/private/api/client/gift-card/balances`, {
          headers: this.authHeaders(),
        })
        if (res && res.ok) {
          this.balances = await res.json()
        }
      } catch (e) {
        // The balance is informational; a failure must not break the page.
      }
    },

    openHistory(balance) {
      this.historyCurrency = balance.currency
      this.historyOptions = { page: 1, rowsPerPage: this.historyOptions.rowsPerPage || 25 }
      this.showHistory = true
      this.getHistory()
    },

    closeHistory() {
      this.showHistory = false
      this.historyCurrency = ''
      this.history = []
      this.historyTotal = undefined
    },

    onHistoryOptions(opts) {
      this.historyOptions = opts
      this.getHistory()
    },

    async getHistory() {
      if (!this.historyCurrency) return
      this.historyLoading = true
      try {
        const params = new URLSearchParams()
        params.append('currency', this.historyCurrency)
        params.append('page', (this.historyOptions.page || 1) - 1)
        params.append('size', this.historyOptions.rowsPerPage || 25)

        const res = await this.$apiFetch(
          `${this.apiUrl()}/private/api/client/gift-card/balances/history?${params.toString()}`,
          { headers: this.authHeaders() }
        )
        if (res && res.ok) {
          const page = await res.json()
          this.history = page.content || []
          this.historyTotal = page.totalElements
        }
      } catch (e) {
        // History is informational; a failure must not break the page.
      } finally {
        this.historyLoading = false
      }
    },

    typeVariant(type) {
      if (type === 'DEPOSIT') return 'success'
      if (type === 'REFUND') return 'warning'
      if (type === 'PURCHASE') return 'secondary'
      return 'outline'
    },

    /**
     * Publishes or withdraws a brand from the fintech app.
     *
     * <p>The row is updated from the server's response rather than optimistically, so the switch
     * can never show a state the backend did not accept.
     */
    async setActivated(brand, activated) {
      if (this.togglingId) return
      this.togglingId = brand.id
      try {
        const action = activated ? 'activate' : 'deactivate'
        const res = await this.$apiFetch(
          `${this.apiUrl()}/private/api/client/gift-card/catalogue/${brand.id}/${action}`,
          { method: 'POST', headers: this.authHeaders() }
        )
        if (res && res.ok) {
          const updated = await res.json()
          const index = this.catalogue.findIndex((b) => b.id === brand.id)
          if (index !== -1) this.catalogue.splice(index, 1, updated)
          toast.success(activated ? `${brand.name} is now live` : `${brand.name} withdrawn`)
        } else {
          const text = await res.text().catch(() => '')
          toast.error(text || 'Could not update this gift card.')
        }
      } catch (e) {
        toast.error('Could not reach the server. Please try again.')
      } finally {
        this.togglingId = ''
      }
    },
  },
}
</script>